import { defaultOptionalConfig } from "./defaults";
import { deepMerge } from "./deep-merge";
import type {
  DeepPartial,
  OptionalRemittanceConfig,
  RemittanceConfig,
  RequiredRemittanceConfig,
} from "./types";

/**
 * Create a remittance config by providing required values and optionally overriding defaults.
 *
 * @param config - Configuration object with required and optional values
 * @returns Complete remittance configuration
 *
 * @example
 * ```ts
 * // apps/sr-client-website/src/config/remittance.ts
 * import { createRemittanceConfig } from "cy-remittance-config";
 *
 * export const remittanceConfig = createRemittanceConfig({
 *   companyName: "Shoprite Send",
 *   shortCompanyName: "ShopriteSend",
 *   contactDetails: {
 *     supportEmail: "help@shopritesend.co.za",
 *     supportPhone: {
 *       display: "+27 11 214 1743",
 *       value: "+27112141743",
 *     },
 *   },
 * });
 * ```
 */
export function createRemittanceConfig(
  config: RequiredRemittanceConfig & DeepPartial<OptionalRemittanceConfig>,
): RemittanceConfig {
  const { companyName, shortCompanyName, ...optionalOverrides } = config;

  const optionalConfig = deepMerge(
    defaultOptionalConfig as unknown as Record<string, unknown>,
    optionalOverrides as DeepPartial<Record<string, unknown>>,
  );

  // Type assertion is safe because deepMerge with defaultOptionalConfig
  // guarantees contactDetails will always be defined
  return {
    companyName,
    shortCompanyName,
    ...optionalConfig,
  } as RemittanceConfig;
}
